import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    AppBar,
    Chip,
    IconButton,
    Button,
} from '@material-ui/core';
import { Icon as MdiIcon } from '@mdi/react';
import { mdiClipboardOutline, mdiShieldLockOpenOutline, mdiShieldLock } from '@mdi/js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import {
    BidProcessType,
    BidProfileStatus,
    EmployeeReportType,
    PeriodType,
    SessionStorageKeys,
} from '@enums/index';
import { safeJsonParse } from '@commonClient2/services/json';
import { selectedBiddableProfile, lockAwardedShiftline, unlockAwardedShiftline } from '../store/bidProfile';
import './BidProfileSummary.scss';
import { getProfileStatusLabel } from '../../Enums/Helpers/LabellingHelper';
import { getProfileStatusColor } from '../../Enums/Helpers/StylingHelper';
import { getReportOptions } from '../services/utilities/reports';
import {
    GroupedIconButton, EmployeeReport, ShiftLineReportTableMobile, ProtectedStateIcon,
} from '../../components';
import { DAY_COLUMNS_COUNT } from '../../Manager/BidProfileEdit/ScheduleSelection/ShiftlineTable/ShiftlineTable';
import LockAwardingConfirmation from '../components/LockAwardingConfirmation';

/**
 * @param {{
*   onBack: () => void
* }} param0
*/
function BidProfileSummaryMobile({ onBack = () => { } }) {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding');

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeBidProfile: bidProfile, employeeBidSelection } = useSelector((state) => state);
    const {
        name,
        anchorDay,
        status,
        profileId,
        isEnableEmployeeBidResultReport,
        isEnableEmployeeRankReport,
        bidProcessType,
        isRebidAllowed,
        sequentialSchedule,
        dayCount,
        periodType,
        approvedShiftlineInfos,
        lockedEmployeeIds,
        employeeRank,
        employeeRankFullNames,
    } = bidProfile;

    const isLocked = approvedShiftlineInfos !== null && approvedShiftlineInfos.isLocked;
    const isLockable = approvedShiftlineInfos !== null && approvedShiftlineInfos.isLockable;

    const { shifts: shiftsDictionary, items, employeeSelections } = employeeBidSelection;
    let maxNbWeek = 1;
    const isSequential = bidProcessType === BidProcessType.Sequential;

    const employeeFilters = employeeRankFullNames.map((employee) => ({
        employeeId: employee.employeeId,
        name: employee.fullName,
        rank: employee.rank,
    }));

    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} itemsToPrepare
     * @returns {EmployeeBidProfile.IGroupedScheduleTemplateItems[]}
     */
    const prepareTableItems = (itemsToPrepare) => {
        maxNbWeek = Math.max(...itemsToPrepare.map((x) => x.nbWeek));
        const res = itemsToPrepare.map((item) => {
            // @ts-ignore
            const weeklyHeight = Object.keys(item.maxDayPerWeek).map((x) => item.maxDayPerWeek[x]);
            /** @type {BidProfile.ISchedulePatternTemplateItem[][][]} */
            const shifts = [];
            item.schedulePatternTemplate.items.forEach((shift) => {
                const week = Math.floor((shift.day - 1) / 7);
                const dayIndex = (shift.day - 1) % 7;
                if (shifts[week] === undefined) {
                    shifts[week] = [[], [], [], [], [], [], []];
                }

                shifts[week][dayIndex].push(shift);
            });

            return {
                ...item,
                weeklyHeight,
                shifts,
            };
        });
        return res;
    };
    const preparedSchedulePatternItems = prepareTableItems(items);
    const [schedulePatternItems, setSchedulePatternItems] = useState(prepareTableItems(preparedSchedulePatternItems));
    const [showReport, setShowReport] = useState(EmployeeReportType.None);
    const [showProtectModal, setShowProtectModal] = useState(false);
    const isBidProfileActive = ![BidProfileStatus.Closed, BidProfileStatus.Published, BidProfileStatus.Canceled].includes(status);

    /**
     * @type {import("../../components/GroupedIconButton/GroupedIconButton").GroupedIconButtonOption[]}}
     */
    const reportOptions = getReportOptions({
        t, isSequential, isEnableEmployeeRankReport, isEnableEmployeeBidResultReport, setShowReport, employeeId: employeeRank.employeeId,
    });

    const handleProtectAssignedShiftline = () => {
        setShowProtectModal(true);
    };

    const confirmProtectAssignedShiftline = () => {
        if (isLocked) {
            dispatch(unlockAwardedShiftline(profileId, true));
        } else {
            dispatch(lockAwardedShiftline(profileId, true));
        }
        setShowProtectModal(false);
    };

    const generateScheduleRows = () => {
        const approvedBid = schedulePatternItems.find((x) => x.approvedRow);
        return (
            <div>
                {
                    approvedBid && (
                        <div className="approvedShiftContainer">
                            <h5 className="shiftContainer">
                                {t('employee.summary.approvedSelection')}
                                {isLockable && <ProtectedStateIcon isProtected={isLocked} />}
                            </h5>
                            <div className="lockButtonContainer">
                                {isLockable && (
                                    <Button
                                        className="bps-protect-button"
                                        variant="outlined"
                                        onClick={handleProtectAssignedShiftline}
                                    >
                                        <MdiIcon
                                            path={isLocked ? mdiShieldLockOpenOutline : mdiShieldLock}
                                            size={1}
                                            style={{ paddingRight: '5px' }}
                                        />
                                        {isLocked ? 'Remove protection' : 'Protect your selection'}
                                    </Button>
                                )}
                            </div>
                            <ShiftLineReportTableMobile
                                shiftLines={[approvedBid]}
                                shiftsDictionary={shiftsDictionary}
                                employeeSelections={employeeSelections}
                                anchorDay={anchorDay}
                                showAwarding
                                showOrder
                                isMultiSelection={!isSequential}
                                maxNbWeek={maxNbWeek}
                                t={t}
                                daysPerPeriod={DAY_COLUMNS_COUNT}
                                periodType={periodType}
                                dayCount={dayCount}
                            />
                        </div>
                    )
                }
                <div>
                    <h5 className="shiftContainer">
                        {t('employee.summary.bidSelection')}
                    </h5>
                    <ShiftLineReportTableMobile
                        shiftLines={schedulePatternItems}
                        shiftsDictionary={shiftsDictionary}
                        employeeSelections={employeeSelections}
                        anchorDay={anchorDay}
                        showAwarding
                        showOrder
                        isMultiSelection={!isSequential}
                        maxNbWeek={maxNbWeek}
                        t={t}
                        daysPerPeriod={DAY_COLUMNS_COUNT}
                        periodType={periodType}
                        dayCount={dayCount}
                    />
                </div>
            </div>
        );
    };
    const handleEditSelection = () => {
        dispatch(selectedBiddableProfile(profileId));
    };

    useEffect(() => {
        setSchedulePatternItems(prepareTableItems(items));
    }, [JSON.stringify(items)]);

    return (
        <div>
            <div>
                <div className="bps-shifts-list-container">
                    <div className="topMenuBar">
                        <div className="topMenuFirstRow">
                            <div className="bpsm-leftSide">
                                <IconButton onClick={() => onBack()}>
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </IconButton>
                                <p className="headline-h-700 bps-bid-name">{name}</p>
                            </div>
                            <div className="bpsm-rightSide">
                                <Chip
                                    label={getProfileStatusLabel(status)}
                                    variant="outlined"
                                    style={getProfileStatusColor(status)}
                                />
                                {
                                    !!(reportOptions.length) && (
                                        <GroupedIconButton
                                            icon={(
                                                <MdiIcon
                                                    path={mdiClipboardOutline}
                                                    size={1}
                                                />
                                            )}
                                            buttonClassName="bps-report-button"
                                            tooltipTitle={t('common.reports.reports')}
                                            options={reportOptions}
                                        />
                                    )
                                }
                            </div>
                        </div>
                    </div>
                    <div>
                        {schedulePatternItems?.length ? generateScheduleRows()
                            : (
                                <div className="shiftline-table-replacement">{t('employee.summary.noSelection')}</div>
                            )}
                    </div>
                </div>
            </div>
            {isBidProfileActive && (
                <AppBar
                    position="fixed"
                    className="bps-fixed-button-container"
                >
                    <button
                        type="button"
                        onClick={() => handleEditSelection()}
                    >
                        Edit Selection
                    </button>
                </AppBar>
            )}
            <EmployeeReport
                profileId={profileId}
                employeeReport={showReport}
                onClose={() => setShowReport(EmployeeReportType.None)}
                dataviewEmployeeData={[]}
                filteredDataviewEmployeeData={[]}
                dataviewFields={[]}
                anchorDay={anchorDay}
                isRebid={isRebidAllowed}
                showScheduledDateAndTime={isSequential}
                sequentialSchedule={sequentialSchedule}
                dayCount={dayCount}
                isPeriodTypeDay={periodType === PeriodType.Days}
                lockedEmployeeIds={lockedEmployeeIds}
                employeeFilters={employeeFilters}
            />
            <LockAwardingConfirmation
                isOpen={showProtectModal}
                setIsOpen={setShowProtectModal}
                onConfirm={confirmProtectAssignedShiftline}
                isLocked={isLocked}
            />
        </div>
    );
}
export default BidProfileSummaryMobile;
